import { useCallback, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Modal } from "antd"
import { useTranslation } from "react-i18next"
import { updateTrip } from "store/trips"
import { serialize } from "object-to-formdata"
import { toaster } from "components/common/Toast"
import { modelSelector } from "store/selectors"
import { setFormTouched } from "store/proNewListingForms"

/**
 * Custom hook to handle auto-save when switching tabs
 * Ensures data is saved before navigating to a different tab
 * 
 * Note: We use local useState for isSaving because updateTrip action
 * doesn't have onStart handler, so Redux loading state isn't set
 * 
 * @param {string} tripId - The ID of the trip/experience being edited
 * @param {boolean} isEditMode - Whether we're in edit mode
 * @returns {Object} - Object with beforeTabChange function and isSaving state
 */
export const useTabSwitchSave = (tripId, isEditMode) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const forms = useSelector((state) => state.proNewListingForms) || {}
    const trip = useSelector(modelSelector("trip"))
    const [isSaving, setIsSaving] = useState(false)

    /**
     * Get the current tab name from pathname
     * @param {string} pathname - Current pathname
     * @returns {string} - Tab name (details, media, scheduling, resources, location)
     */
    const getCurrentTab = useCallback((pathname) => {
        const tabId = pathname.split("/").findLast(Boolean)
        const tabMap = {
            "details": "details",
            "media": "media",
            "scheduling": "scheduling",
            "resources": "resources",
            "location": "location"
        }
        return tabMap[tabId] || "details"
    }, [])

    /**
     * Check if current tab has required fields missing
     * @param {string} tabName - Name of the tab to validate
     * @returns {Object} - { isValid: boolean, missingFields: string[] }
     */
    const validateTab = useCallback((tabName) => {
        const formData = forms[tabName]
        const values = formData?.values || {}
        const missingFields = []

        switch (tabName) {
            case "details":
                // Check title (from Redux or trip data)
                const title = values.title?.trim() || trip?.title?.trim()
                if (!title) {
                    missingFields.push(t("experiences.details.labels.title"))
                }

                // Check description (from Redux or trip data)
                const description = values.description?.trim() || trip?.description?.trim()
                if (!description) {
                    missingFields.push(t("experiences.details.labels.description"))
                }

                // Check categories (from Redux or trip data)
                const categories = values.categories || trip?.categories
                if (!categories || (Array.isArray(categories) && categories.length === 0)) {
                    missingFields.push(t("experiences.details.labels.categories"))
                }
                break


            case "media": {
                // Check if at least 2 media items exist (not marked for deletion)
                // Media component stores count in Redux to avoid File object mutation errors
                const mediaCount = values.mediaCount

                // Fallback to checking trip.media if count not available
                if (mediaCount !== undefined) {
                    if (mediaCount < 2) {
                        missingFields.push(t("experiences.media.minimum_photos"))
                    }
                } else {
                    const media = trip?.media || []
                    const activeMedia = media.filter((item) => !item._destroy)
                    if (activeMedia.length < 2) {
                        missingFields.push(t("experiences.media.minimum_photos"))
                    }
                }
                break
            }





            case "scheduling": {
                // Check 1: At least one trip price exists
                const selectedPricingType = values.pricing_type || trip?.pricing_type || "per_person"
                const tripPrices = (trip?.trip_prices || []).filter((p) => !p.pricing_type || p.pricing_type === selectedPricingType)

                if (tripPrices.length === 0) {
                    missingFields.push(t("experiences.scheduling.sections.length_and_rates.title"))
                }


                // Check 2: At least one day is selected
                // Merge values.available_days with trip.available_days (trip takes precedence for true values)
                const reduxDays = values.available_days || {}
                const tripDays = trip?.available_days || {}
                const availableDays = { ...reduxDays, ...tripDays }

                let hasAvailableDays = false

                if (Array.isArray(availableDays)) {
                    hasAvailableDays = availableDays.length > 0
                } else if (availableDays && typeof availableDays === "object") {
                    hasAvailableDays = Object.values(availableDays).some(Boolean)
                }

                if (!hasAvailableDays) {
                    missingFields.push(t("experiences.scheduling.sections.days_and_hours.title"))
                }


                // Check 3: At least one time block exists
                const availableHours = values.available_hours || trip?.available_hours
                if (!availableHours || availableHours.length === 0 || !availableHours[0]?.start || !availableHours[0]?.end) {
                    missingFields.push(t("experiences.scheduling.sections.days_and_hours.time_blocks"))
                }


                // Check 4: Minimum guests is set
                const minGuests = values.min_guests || trip?.min_guests
                if (!minGuests || minGuests < 1) {
                    missingFields.push(t("experiences.details.labels.min_guests"))
                }

                // Check 5: Maximum guests is set
                const maxGuests = values.max_guests || trip?.max_guests
                if (!maxGuests || maxGuests < 1) {
                    missingFields.push(t("experiences.details.labels.max_guests"))
                }

                // Check 6: Deposit percentage is set (if not requiring full payment)
                const fullPaymentRequired = values.full_payment_required ?? trip?.full_payment_required
                const depositPercentage = values.deposit_percentage ?? trip?.deposit_percentage

                if (!fullPaymentRequired) {
                    if (!depositPercentage || depositPercentage < 10 || depositPercentage > 50) {
                        missingFields.push(t("experiences.scheduling.sections.length_and_rates.deposit"))
                    }
                }

                // Check 7: Seasonal date ranges (if seasonal experience)
                const isSeasonal = values.seasonal ?? trip?.seasonal
                if (isSeasonal) {
                    // Check all seasonStart_* and seasonEnd_* fields
                    const seasonalFields = Object.keys(values).filter(key =>
                        key.startsWith('seasonStart_') || key.startsWith('seasonEnd_')
                    )

                    // Get unique block indices
                    const blockIndices = new Set()
                    seasonalFields.forEach(key => {
                        const match = key.match(/season(?:Start|End)_(\d+)/)
                        if (match) blockIndices.add(match[1])
                    })

                    // Validate each block has both start and end dates
                    let hasIncompleteDateRange = false
                    blockIndices.forEach(index => {
                        const startDate = values[`seasonStart_${index}`]
                        const endDate = values[`seasonEnd_${index}`]
                        if (!startDate || !endDate) {
                            hasIncompleteDateRange = true
                        }
                    })

                    // If no blocks at all or any block is incomplete
                    if (blockIndices.size === 0 || hasIncompleteDateRange) {
                        missingFields.push(t("experiences.scheduling.sections.seasonal_dates"))
                    }
                }

                break
            }

            case "resources":
                // Resources are optional, no strict validation
                break

            case "location":
                // Check address (from Redux or trip data)
                const address = values.address?.trim() || trip?.address?.trim()
                if (!address) {
                    missingFields.push(t("experiences.location.labels.address"))
                }
                break

            default:
                break
        }


        return {
            isValid: missingFields.length === 0,
            missingFields
        }
    }, [forms, trip, t])

    /**
     * Save current tab data before switching
     * @param {string} currentTabName - Name of current tab
     * @returns {Promise<boolean>} - Success status
     */
    const saveCurrentTab = useCallback(async (currentTabName) => {
        const formData = forms[currentTabName]

        // Don't save if form hasn't been touched
        if (!formData?.touched) {
            return true
        }

        //Don't save if already saving
        if (isSaving) {
            return false
        }

        try {
            setIsSaving(true)
            const values = formData.values || {}

            // Prepare payload based on tab
            let payload = {}
            let serializedFormData

            switch (currentTabName) {
                case "media": {
                    // Media requires special handling - delegate to component's onFinish
                    // which has access to localMedia state with all changes (deletions + new uploads)
                    try {
                        await new Promise((resolve, reject) => {
                            const event = new CustomEvent('mediaSaveRequest', {
                                detail: { resolve, reject }
                            })
                            window.dispatchEvent(event)
                        })

                        if (!isEditMode) {
                            toaster.success(t("experiences.auto_save.draft_saved"))
                        }
                        return true
                    } catch (error) {
                        console.error("Error saving media:", error)
                        toaster.error(t("experiences.auto_save.save_failed"))
                        return false
                    } finally {
                        setIsSaving(false)
                    }
                }

                case "scheduling": {
                    payload = { ...values }

                    // Normalize available_days to ensure selected days send true
                    if (values.available_days && typeof values.available_days === "object") {
                        payload.available_days = Object.keys(values.available_days).reduce((acc, day) => {
                            acc[day] = !!values.available_days[day]
                            return acc
                        }, {})
                    }

                    // Format available_hours (dayjs objects to HH:mm strings)
                    if (values.available_hours && values.available_hours.length > 0) {
                        payload.available_hours = values.available_hours.map(({ start, end }) => ({
                            start: start?.format ? start.format("HH:mm") : start,
                            end: end?.format ? end.format("HH:mm") : end
                        }))

                        // Also create trip_slots_attributes for backend
                        payload.trip_slots_attributes = values.available_hours.map(({ start, end }) => ({
                            start_time: start?.format ? start.format("HH:mm") : start,
                            end_time: end?.format ? end.format("HH:mm") : end
                        }))
                    }

                    // Format seasonal date ranges (if seasonal)
                    if (values.seasonal) {
                        payload.seasonal = true // Explicitly set seasonal status
                        const seasonalDateRanges = []

                        // Find all block indices from keys like 'seasonStart_0', 'seasonStart_1'
                        const indices = new Set()
                        Object.keys(values).forEach(key => {
                            const match = key.match(/seasonStart_(\d+)/)
                            if (match) indices.add(match[1])
                        })

                        indices.forEach(index => {
                            const start = values[`seasonStart_${index}`]
                            const end = values[`seasonEnd_${index}`]

                            if (start && end) {
                                const startStr = start.format ? start.format("YYYY-MM-DD") : start
                                const endStr = end.format ? end.format("YYYY-MM-DD") : end
                                seasonalDateRanges.push({ start: startStr, end: endStr })
                            }
                        })

                        if (seasonalDateRanges.length > 0) {
                            payload.available_date_ranges = seasonalDateRanges
                            // Also set seasonal_trips_attributes (for backend compatibility)
                            payload.seasonal_trips_attributes = seasonalDateRanges.map(range => ({
                                start_date: range.start,
                                end_date: range.end
                            }))
                        }

                        // Convert recurringYearly (camelCase) to recurring_yearly (snake_case) for API
                        if ('recurringYearly' in values) {
                            payload.recurring_yearly = values.recurringYearly
                            delete payload.recurringYearly
                        }
                    } else {
                        // Explicitly set seasonal to false when not seasonal
                        payload.seasonal = false
                    }

                    serializedFormData = serialize({ trip: payload }, { allowEmptyArrays: true })
                    break
                }

                case "details":
                case "resources":
                case "location":
                default:
                    // For details, resources, location, and any other tabs, use simple payload
                    payload = { ...values }
                    serializedFormData = serialize({ trip: payload }, { allowEmptyArrays: true })
                    break
            }

            await dispatch(updateTrip(tripId, serializedFormData))
            dispatch(setFormTouched(currentTabName, false))

            // Show success message only in create mode
            if (!isEditMode) {
                toaster.success(t("experiences.auto_save.draft_saved"))
            }

            return true
        } catch (error) {
            console.error("Error saving tab data:", error)
            toaster.error(t("experiences.auto_save.save_failed"))
            return false
        } finally {
            setIsSaving(false)
        }
    }, [forms, dispatch, tripId, isEditMode, t, isSaving, trip])

    /**
     * Handle before tab change - validate and save
     * @param {string} currentPathname - Current pathname
     * @param {Function} navigateCallback - Function to call to proceed with navigation
     * @returns {Promise<void>}
     */
    const beforeTabChange = useCallback(async (currentPathname, navigateCallback) => {
        const currentTab = getCurrentTab(currentPathname)
        const formData = forms[currentTab]

        // In edit mode, if form values are empty, it means forms haven't been initialized yet after refresh
        // Skip validation and save in this case to prevent false errors
        const isFormInitialized = formData?.values && Object.keys(formData.values).length > 0
        if (isEditMode && !isFormInitialized) {
            navigateCallback()
            return
        }

        // In edit mode, only save if the form has been touched (user made changes)
        // In create mode, always validate required tabs
        const alwaysValidateTabs = ["details", "media", "scheduling", "location"]

        if (isEditMode) {
            // Edit mode: only validate and save if form was touched
            if (!formData?.touched) {
                navigateCallback()
                return
            }
        } else {
            // Create mode: skip validation for non-required tabs if not touched
            if (!formData?.touched && !alwaysValidateTabs.includes(currentTab)) {
                navigateCallback()
                return
            }
        }

        // Validate current tab
        const validation = validateTab(currentTab)

        // If validation fails, show error modal and block navigation
        if (!validation.isValid) {
            Modal.error({
                title: t("experiences.validation.incomplete_section"),
                content: (
                    <div>
                        <p>{t("experiences.validation.missing_fields_message")}</p>
                        <ul style={{ marginTop: "8px", paddingLeft: "20px" }}>
                            {validation.missingFields.map((field) => (
                                <li key={field}>{field}</li>
                            ))}
                        </ul>
                        <p style={{ marginTop: "12px" }}>
                            {t("experiences.validation.please_complete_required_fields")}
                        </p>
                    </div>
                ),
                okText: t("global.ok")
            })
            // Block navigation - don't call navigateCallback
            return
        }

        // Validation passed - save and navigate
        const saved = await saveCurrentTab(currentTab)

        if (saved) {
            navigateCallback()
        } else {
            // Save failed - show error but don't allow navigation
            Modal.error({
                title: t("experiences.auto_save.save_failed"),
                content: t("experiences.auto_save.please_try_again"),
                okText: t("global.ok")
            })
        }
    }, [getCurrentTab, validateTab, saveCurrentTab, forms, t, isEditMode])



    return {
        beforeTabChange,
        saveCurrentTab,
        validateTab,
        isSaving
    }
}
